import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { artist, services } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Linkedin, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", company: "", project: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Scroll to form when arriving via the #contact-form hash from "Start a project" CTAs
  useEffect(() => {
    if (location.hash === "#contact-form") {
      const t = setTimeout(() => {
        const el = document.getElementById("contact-form");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
  }, [location.hash, location.key]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. I\u2019ll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", project: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : (detail || "Something went wrong. Please try again or email me directly.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24">
      <header className="mb-16 md:mb-20 fade-up">
        <p className="font-mono-label text-[#F62493] mb-5">Contact</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em] text-stone-100 max-w-4xl">
          Let&apos;s make something <em className="italic text-[#F62493]">people remember.</em>
        </h1>
      </header>

      <div className="grid md:grid-cols-12 gap-12 lg:gap-20">
        {/* Contact info */}
        <aside className="md:col-span-4 space-y-10 fade-up delay-1">
          <div>
            <p className="font-mono-label text-stone-300 mb-3">Direct</p>
            <a href={`mailto:${artist.email}`} className="font-display text-2xl md:text-3xl text-stone-100 hover:text-[#F62493] transition-colors block">
              {artist.email}
            </a>
          </div>

          <div>
            <p className="font-mono-label text-stone-300 mb-3">Location</p>
            <p className="flex items-start gap-3 text-stone-200">
              <MapPin size={18} className="mt-1 text-[#F62493]" />
              <span>{artist.location}<br /><span className="text-stone-300 text-sm">Remote-friendly worldwide</span></span>
            </p>
          </div>

          <div>
            <p className="font-mono-label text-stone-300 mb-3">Elsewhere</p>
            <ul className="space-y-3">
              <li><a href={artist.socials.linkedin} className="inline-flex items-center gap-3 link-underline text-stone-200"><Linkedin size={16} /> LinkedIn</a></li>
              <li><a href={`mailto:${artist.email}`} className="inline-flex items-center gap-3 link-underline text-stone-200"><Mail size={16} /> Email</a></li>
            </ul>
          </div>

          <div>
            <p className="font-mono-label text-stone-300 mb-3">What I help with</p>
            <ul className="flex flex-wrap gap-2">
              {services.map((s) => (
                <li key={s} className="font-mono-label text-xs px-3 py-1.5 border border-stone-400 rounded-full text-stone-200">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Form */}
        <form id="contact-form" onSubmit={submit} className="md:col-span-8 space-y-6 fade-up delay-2">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="font-mono-label text-stone-300">Your Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)}
                className="mt-2 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg" />
            </div>
            <div>
              <Label htmlFor="email" className="font-mono-label text-stone-300">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                className="mt-2 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="company" className="font-mono-label text-stone-300">Company / Brand</Label>
              <Input id="company" value={form.company} onChange={(e) => update("company", e.target.value)}
                className="mt-2 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg" />
            </div>
            <div>
              <Label htmlFor="project" className="font-mono-label text-stone-300">Project Type</Label>
              <Input id="project" placeholder="Packaging, event signage, rebrand..." value={form.project}
                onChange={(e) => update("project", e.target.value)}
                className="mt-2 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg placeholder:text-stone-400" />
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="font-mono-label text-stone-300">Tell me about it *</Label>
            <Textarea id="message" rows={6} value={form.message} onChange={(e) => update("message", e.target.value)}
              placeholder="Timelines, goals, vibes, references…"
              className="mt-2 bg-transparent border-0 border-b border-stone-400 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#F62493] text-lg resize-none placeholder:text-stone-400" />
          </div>

          <div className="flex items-center justify-between pt-4">
            <p className="font-mono-label text-stone-300 text-xs max-w-sm">
              I reply to every inquiry personally within one business day.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-[#171717] text-[#F7F3ED] px-7 py-3 hover:bg-[#F62493] disabled:opacity-60 transition-colors"
            >
              {submitting ? "Sending..." : (<>Send message <Send size={16} /></>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactPage;
